import React, { useState } from 'react';

interface SaveNotificationModalProps {
  venueName: string;
  venueId?: string;
  city?: string;
  onClose: () => void;
}

const SaveNotificationModal: React.FC<SaveNotificationModalProps> = ({ venueName, venueId, city, onClose }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);

      const res = await fetch('/.netlify/functions/save-notification-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          venue_id: venueId,
          venue_name: venueName,
          city,
          source: venueId ? 'favorite' : 'launch',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save notification preference.');

      setSubmitted(true);
      setTimeout(() => onClose(), 2500);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong while saving your preference.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-10 right-4 md:right-10 z-[200] w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-10 fade-in duration-500">
      <div className="bg-black text-white p-8 rounded-[2.5rem] shadow-2xl border border-white/10 grainy relative overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {!submitted ? (
          <>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-amber-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg shadow-amber-500/20">🔔</div>
              <div>
                <h4 className="text-lg font-black serif italic tracking-tight">Stay Updated.</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Notification Opt-in</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-300 leading-relaxed mb-8 font-medium">
              Want a quick ping when <span className="text-white font-black">{venueName}</span>{' '}
              {venueId ? 'drops a Flash Boost?' : 'launches in your area?'}
            </p>

            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12 bg-white/10 border border-white/5 rounded-xl px-4 text-xs font-bold outline-none focus:border-amber-500 transition-all placeholder-white/20"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-6 h-12 bg-amber-500 text-black font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-white transition-all active:scale-95 shadow-lg disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Notify Me'}
              </button>
            </form>
            {error && <p className="mt-3 text-[10px] font-bold text-red-400 uppercase tracking-wider">{error}</p>}
            <p className="mt-4 text-[8px] font-bold text-gray-500 uppercase tracking-widest text-center">
              Only updates for this venue. No spam, ever.
            </p>
          </>
        ) : (
          <div className="text-center py-4 flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center text-3xl animate-bounce">✨</div>
            <h4 className="text-xl font-black serif">You're on the list!</h4>
            <p className="text-xs text-gray-400 font-medium">We'll alert you if {venueName} has a flash update.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveNotificationModal;
